import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const SystemStatusBanner = () => {
  const [systemStatus, setSystemStatus] = useState({
    carrier: 'operational',
    database: 'operational',
    validation: 'operational'
  });
  const [isDismissed, setIsDismissed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const checkSystemStatus = () => {
      const statuses = ['operational', 'degraded', 'outage'];
      const randomStatus = () => statuses?.[Math.floor(Math.random() * statuses?.length)];
      
      setSystemStatus({
        carrier: randomStatus(),
        database: 'operational',
        validation: randomStatus()
      });
    };

    checkSystemStatus();
    const interval = setInterval(checkSystemStatus, 30000);

    return () => clearInterval(interval);
  }, []);

  const hasIssues = Object.values(systemStatus)?.some(status => status !== 'operational');

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'text-success';
      case 'degraded':
        return 'text-warning-foreground';
      case 'outage':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return 'CheckCircle2';
      case 'degraded':
        return 'AlertTriangle';
      case 'outage':
        return 'XCircle';
      default:
        return 'HelpCircle';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'operational':
        return 'Operational';
      case 'degraded':
        return 'Degraded Performance';
      case 'outage':
        return 'Service Outage';
      default:
        return 'Unknown';
    }
  };

  const getBannerMessage = () => {
    if (systemStatus?.carrier !== 'operational') {
      return 'Carrier API connectivity issues may affect tracking validation';
    }
    if (systemStatus?.validation !== 'operational') {
      return 'Validation service experiencing delays';
    }
    return 'System experiencing connectivity issues';
  };

  if (!hasIssues || isDismissed) {
    return null;
  }

  return (
    <div className="fixed top-[60px] left-0 right-0 z-150 bg-warning/10 border-b border-warning/20">
      <div className="max-w-screen-2xl mx-auto px-24 py-18">
        <div className="flex items-start justify-between gap-24">
          <div className="flex items-start gap-18 flex-1">
            <Icon 
              name="AlertTriangle" 
              size={24} 
              color="var(--color-warning-foreground)" 
              strokeWidth={2}
              className="flex-shrink-0 mt-[2px]" 
            />
            <div className="flex-1">
              <div className="flex items-center gap-12 mb-6">
                <span className="font-body font-semibold text-[15px] text-warning-foreground">
                  System Status Alert
                </span>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="font-caption text-[13px] text-warning-foreground underline hover:no-underline transition-smooth"
                  aria-expanded={showDetails}
                >
                  {showDetails ? 'Hide Details' : 'View Details'}
                </button>
              </div>
              <p className="font-body text-[14px] text-warning-foreground/90 leading-relaxed">
                {getBannerMessage()}
              </p>

              {showDetails && (
                <div className="mt-18 grid grid-cols-1 md:grid-cols-3 gap-18">
                  <div className="flex items-center gap-12 px-18 py-12 bg-card rounded-lg border border-border">
                    <Icon 
                      name={getStatusIcon(systemStatus?.carrier)} 
                      size={20} 
                      color={`var(--color-${systemStatus?.carrier === 'operational' ? 'success' : systemStatus?.carrier === 'degraded' ? 'warning' : 'error'})`}
                      strokeWidth={2} 
                    />
                    <div className="flex-1">
                      <div className="font-caption font-medium text-[12px] text-muted-foreground uppercase tracking-wide">
                        Carrier API
                      </div>
                      <div className={`font-body font-medium text-[14px] ${getStatusColor(systemStatus?.carrier)}`}>
                        {getStatusText(systemStatus?.carrier)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-12 px-18 py-12 bg-card rounded-lg border border-border">
                    <Icon 
                      name={getStatusIcon(systemStatus?.database)} 
                      size={20} 
                      color={`var(--color-${systemStatus?.database === 'operational' ? 'success' : systemStatus?.database === 'degraded' ? 'warning' : 'error'})`}
                      strokeWidth={2} 
                    />
                    <div className="flex-1">
                      <div className="font-caption font-medium text-[12px] text-muted-foreground uppercase tracking-wide">
                        Database
                      </div>
                      <div className={`font-body font-medium text-[14px] ${getStatusColor(systemStatus?.database)}`}>
                        {getStatusText(systemStatus?.database)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-12 px-18 py-12 bg-card rounded-lg border border-border">
                    <Icon 
                      name={getStatusIcon(systemStatus?.validation)} 
                      size={20} 
                      color={`var(--color-${systemStatus?.validation === 'operational' ? 'success' : systemStatus?.validation === 'degraded' ? 'warning' : 'error'})`}
                      strokeWidth={2} 
                    />
                    <div className="flex-1">
                      <div className="font-caption font-medium text-[12px] text-muted-foreground uppercase tracking-wide">
                        Validation
                      </div>
                      <div className={`font-body font-medium text-[14px] ${getStatusColor(systemStatus?.validation)}`}>
                        {getStatusText(systemStatus?.validation)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setIsDismissed(true)}
            className="flex-shrink-0 w-[32px] h-[32px] flex items-center justify-center rounded-lg hover:bg-warning/20 transition-smooth"
            aria-label="Dismiss system status alert"
          >
            <Icon 
              name="X" 
              size={18} 
              color="var(--color-warning-foreground)" 
              strokeWidth={2} 
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemStatusBanner;